import { GameRoomState } from "@presidents/game-engine";
import { LocalParticipant, Room } from "livekit-client";
import React from "react";
import styled from "styled-components";
import { useAuth } from "../context/AuthContext";
import { useParticipant } from "../hooks/webrtc/useParticipant";
import { useFaceTracking } from "../hooks/useFaceTracking";
import arrangePlayers from "../utils/arrange-players";
import { Button } from "./Button";
import { Duration } from "./Duration";
import GameRules from "./GameRules";
import Microphone, { MicrophoneOff } from "./icons/Microphone";
import Video, { VideoOff } from "./icons/Video";
import FaceTracking, { FaceTrackingOff } from "./icons/FaceTracking";
import Stack from "./Stack";
import MediaDeviceSelector from "./MediaDeviceSelector";
import { useSharedState } from "../hooks/useSharedState";

const GlobalControlsElement = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding-bottom: var(--bottom-control-bar-height);
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  opacity: 1;
  transition: transform 1s ease-out, opacity 1s ease-out;
  transform-origin: 100% 0;
  will-change: transform, opacity;
  color: #fff;
  font-family: var(--fontFamily);

  text-align: left;

  &[hidden] {
    opacity: 0;
    transform: scale(0);
  }
  overflow: auto;
`;

const GlobalControlsScroll = styled.div`
  height: 100%;
  padding: var(--spacing-large);
  overflow-y: auto;
`;

const GlobaleControlsInner = styled.div`
  max-width: 400px;
  margin: auto;
`;
const GlobalControlsHeading = styled.h2`
  font-family: var(--altFontFamily);
  &:not(:first-child) {
    margin-top: var(--spacing-large);
  }
`;

const GlobalControlsHeadingControl = styled.div`
  display: flex;
  align-items: center;
  > [data-control] {
    margin-left: auto;
  }
`;

const GlobalControlSection = styled.div`
  padding: var(--spacing-small) var(--spacing-default);
  margin: var(--spacing-small) auto;
`;
const GlobalControl = styled.a`
  display: flex;
  align-items: center;
  padding: var(--spacing-small) var(--spacing-default);
  margin: var(--spacing-small) auto;
  touch-action: manipulation;
  cursor: pointer;
  &:active {
    background-color: rgba(0, 0, 0, 0.1);
  }
  &[data-bold] {
    font-weight: bold;
  }
  & > button {
    display: block;
    margin: auto;
    font-size: 16px;
  }
  & small {
    margin-left: auto;
    font-size: 12px;
  }
`;

const GlobalControlsFooter = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: var(--bottom-control-bar-height);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background);
`;

const CommandButton = styled.button`
  border: 0;
  color: inherit;
  border-radius: 50%;
  width: var(--card-height);
  height: var(--card-height);
  background-color: var(--secondary);
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.2);
  &[data-enabled="false"] {
    background-color: var(--primary);
  }
  & + & {
    margin-left: 30px;
  }
  & > svg {
    width: calc(var(--card-height) / 2);
    height: calc(var(--card-height) / 2);
  }
`;

export const GlobalControls: React.FC<{
  gameRoomState: GameRoomState;
  showMenu?: boolean;
  room?: Room | null;
  localParticipant?: LocalParticipant | null;
  requestNewGame: () => void;
  requestQuitGame: () => void;
  requestResetGame: () => void;
  showLeaderBoard: () => void;
}> = ({ gameRoomState, room, localParticipant, ...props }) => {
  const auth = useAuth();
  const user = auth.isSignedIn ? auth.user : null;
  const [userSeed, setUserSeed] = useSharedState(`userSeed:${user?.uid}`, "");
  const { faceTrackingEnabled, setFaceTrackingEnabled } = useFaceTracking();
  const { isAudioMuted, cameraPublication, connectionQuality } = useParticipant(
    localParticipant
  );
  const isHost = user?.uid === gameRoomState.createdBy;
  const players = arrangePlayers(
    user,
    gameRoomState.players,
    gameRoomState.playerRanks,
    0
  );
  return (
    <GlobalControlsElement hidden={props.showMenu ? undefined : true}>
      <GlobalControlsScroll>
        <GlobaleControlsInner>
          {gameRoomState.numGames > 0 && (
            <>
              <GlobalControlsHeading>
                Game {gameRoomState.numGames} of{" "}
                {gameRoomState.options.gameLimit}
              </GlobalControlsHeading>
              <GlobalControl as="div">
                Duration: <Duration start={gameRoomState.startTime} />
              </GlobalControl>
              <GlobalControlSection>
                Rules:
                <GameRules
                  numGames={gameRoomState.numGames}
                  rules={gameRoomState.options.rules}
                />
              </GlobalControlSection>
              <GlobalControl as="div">
                <Button onClick={props.showLeaderBoard}>
                  Show Leaderboard
                </Button>
              </GlobalControl>
            </>
          )}
          {isHost && (
            <GlobalControl as="div">
              <Button
                appearance="secondary"
                onClick={() => {
                  if (
                    window.confirm(
                      "Are you sure you want to reset the all data in this game room? "
                    )
                  ) {
                    props.requestResetGame();
                  }
                }}
              >
                Reset Game
              </Button>
            </GlobalControl>
          )}
          <GlobalControlsHeadingControl>
            <GlobalControlsHeading>Players </GlobalControlsHeading>
            {/* <ToggleSwitch
              size="small"
              appearance="primary"
              label="Mute Everyone"
              checked={everyoneMuted}
              onChange={toggleEveryoneMuted}
            /> */}
          </GlobalControlsHeadingControl>
          {players?.map((p) => {
            const isMe = p.id === user?.uid;
            return (
              <GlobalControl key={p.id} data-bold={isMe || undefined}>
                {p.name} {p.id === user?.uid ? <small>Me</small> : null}
              </GlobalControl>
            );
          })}
          <GlobalControlsHeading>Profile</GlobalControlsHeading>
          <GlobalControl as="div">
            ID <small>{user?.uid}</small>
          </GlobalControl>
          {/* <GlobalControl as="div" onClick={props.showStats}>
            Connection
            {props.showMenu && (
              <small>
                <WebRTCBitRate statsGetter={getStats} />
              </small>
            )}
          </GlobalControl> */}
          <GlobalControl as="div">
            Connection
            <small>{connectionQuality}</small>
          </GlobalControl>
          <GlobalControl as="div">
            <Button
              appearance="secondary"
              onClick={() => {
                if (
                  window.confirm("Are you sure you want to quit the game? ")
                ) {
                  props.requestQuitGame();
                }
              }}
            >
              Quit Game
            </Button>
          </GlobalControl>
          <GlobalControl as="div">
            <Button
              appearance="secondary"
              onClick={() => {
                if (window.confirm("Are you sure you want to logout? ")) {
                  auth.isSignedIn
                    ? auth.signOut()
                    : (window.location.href = "/");
                }
              }}
            >
              Logout
            </Button>
          </GlobalControl>
          <GlobalControl>
            <Stack>
              <MediaDeviceSelector kind="videoinput" />
              <MediaDeviceSelector kind="audioinput" />
            </Stack>
          </GlobalControl>
        </GlobaleControlsInner>
      </GlobalControlsScroll>
      <GlobalControlsFooter>
        <CommandButton
          data-enabled={localParticipant?.isMicrophoneEnabled}
          onClick={() =>
            localParticipant?.setMicrophoneEnabled(
              !localParticipant?.isMicrophoneEnabled
            )
          }
        >
          {localParticipant?.isMicrophoneEnabled ? (
            <Microphone />
          ) : (
            <MicrophoneOff />
          )}
        </CommandButton>
        <CommandButton
          data-enabled={localParticipant?.isCameraEnabled}
          onClick={() => {
            localParticipant?.setCameraEnabled(
              !localParticipant?.isCameraEnabled
            );
            setUserSeed(Date.now() + "");
          }}
        >
          {localParticipant?.isCameraEnabled ? <Video /> : <VideoOff />}
        </CommandButton>
        <CommandButton
          data-enabled={faceTrackingEnabled}
          onClick={() => setFaceTrackingEnabled(!faceTrackingEnabled)}
        >
          {faceTrackingEnabled ? <FaceTracking /> : <FaceTrackingOff />}
        </CommandButton>
      </GlobalControlsFooter>
    </GlobalControlsElement>
  );
};
